import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, webImageUrl as webImg, type Product, type WebPage } from '../api';
import { useAuth } from '../auth';
import { ErrorBox, Loading, Modal, Money, fmtDate } from './ui';

interface RefreshResult {
  url: string;
  sourceName: string;
  outcome: 'read' | 'not_allowed' | 'failed' | 'blocked';
  hasDescription?: boolean;
  photosSaved?: number;
  message?: string;
}

/** Information read from Costco websites for this item number. Shown separately; copied only on request. */
export function WebInfo({ product, onApplied }: { product: Product; onApplied: () => void }) {
  const q = useQuery({
    queryKey: ['productWeb', product.id],
    queryFn: () => api<{ pages: WebPage[] }>(`/products/${product.id}/web`).then((r) => r.pages),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const isAdmin = useAuth().user?.role === 'admin';

  const apply = async (page: WebPage, field: 'title' | 'description') => {
    if (busy) return;
    const current = field === 'title' ? product.listingTitle : product.description;
    if (current && !confirm(`Replace the current ${field === 'title' ? 'listing title' : 'description'} with the one from ${page.sourceName}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/products/${product.id}/apply-web`, { body: { pageId: page.id, field, version: product.version } });
      onApplied();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);
  const refresh = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setRefreshMsg(null);
    try {
      const { results } = await api<{ results: RefreshResult[] }>(`/products/${product.id}/web/refresh`, { method: 'POST' });
      setRefreshMsg(
        results
          .map((r) =>
            r.outcome === 'read'
              ? `${r.sourceName}: read again${r.hasDescription ? '' : ' (no description on the page)'}${r.photosSaved ? `, ${r.photosSaved} photo(s) saved` : ''}${r.message ? ` (${r.message})` : ''}`
              : `${r.sourceName}: ${r.outcome === 'blocked' ? 'stopped, the site refused' : 'not read'}${r.message ? ` (${r.message})` : ''}`,
          )
          .join(' · '),
      );
      await q.refetch();
      onApplied();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const refreshButton = isAdmin && (
    <div className="row gap wrap">
      <button className="btn btn-small" disabled={busy} onClick={refresh}>
        {busy ? 'Reading…' : '↻ Re-read from Costco now'}
      </button>
      {refreshMsg && <span className="small">{refreshMsg}</span>}
    </div>
  );

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data?.length)
    return (
      <div>
        <p className="muted">
          No Costco web page has been found for item {product.itemNumber} yet. Not every warehouse item is sold online.
        </p>
        <ErrorBox error={error} />
      </div>
    );

  return (
    <div>
      {refreshButton}
      <ErrorBox error={error} />
      {q.data.map((p) => (
        <div key={p.id} className="web-page">
          <div className="row between wrap">
            <div>
              <strong>{p.sourceName}</strong>{' '}
              <a href={p.url} target="_blank" rel="noreferrer" className="small">
                open page ↗
              </a>
              <div className="small muted">
                Item {p.sku} · read {fmtDate(p.fetchedAt)}
                {p.breadcrumbs?.length ? ` · ${p.breadcrumbs.join(' › ')}` : ''}
              </div>
            </div>
            <div className="row gap">
              <button className="btn btn-small" disabled={busy || !p.name} onClick={() => apply(p, 'title')}>
                {product.listingTitleSourcePageId === p.id ? '✓ Listing title' : 'Use as listing title'}
              </button>
              <button className="btn btn-small" disabled={busy || (!p.summary && !p.features?.length)} onClick={() => apply(p, 'description')}>
                {product.descriptionSourcePageId === p.id ? '✓ Description' : 'Use as description'}
              </button>
            </div>
          </div>
          <h3 className="web-title">{p.name}</h3>
          <div className="small">
            {p.brand && <>Brand: {p.brand} · </>}
            {p.model && <>Model: {p.model} · </>}
            Costco price when read: <Money amount={p.price} currency={p.currency} />
          </div>
          {p.images.length > 0 && (
            <div className="photo-grid">
              {p.images.map((img) => (
                <div key={img.id} className="photo-cell">
                  <img src={webImg(img.id)} alt="" onClick={() => setViewing(img.id)} />
                </div>
              ))}
            </div>
          )}
          <p className="small muted">These are Costco's catalogue photos of the new product, not photos of your item.</p>
          {p.summary && <p className="prewrap">{p.summary}</p>}
          {!!p.features?.length && (
            <ul>
              {p.features.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          )}
          {!!p.specifications?.length && (
            <details>
              <summary>Specifications ({p.specifications.length})</summary>
              <table className="table small">
                <tbody>
                  {p.specifications.map(([k, v], i) => (
                    <tr key={i}>
                      <th>{k}</th>
                      <td>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      ))}
      {viewing && (
        <Modal title="Costco photo" onClose={() => setViewing(null)}>
          <img src={webImg(viewing, 'full')} alt="" className="photo-full" />
        </Modal>
      )}
    </div>
  );
}
